/**
 * **The booth's paper** (ON11 item 3, `Print.dc.html`): the booth sheet — one per station per
 * day, printed at Open the booth, names by hour with a signature column, the paper fallback the day
 * the server goes away — and the paper waiver pad: the same text as the guest screen, a line to
 * sign, and "entered by" at the foot. Both are read by the station's own session.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatCountDay, formatTime, type PaperRow } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';

interface Sheet {
  label: string;
  day: string;
  shifts: { area: string; startsAt: string; endsAt: string; people: string[] }[];
}

export function BoothSheet(): ReactNode {
  const d = useLoad<Sheet>('/volunteers/booth/sheet');
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading the day…</p>;
  const s = d.data;
  const rows: PaperRow[] = s.shifts.flatMap((x) => {
    const when = `${formatTime(x.startsAt)} – ${formatTime(x.endsAt)}`;
    return [
      ...x.people.map((name, i) => ({
        key: `${x.area}${x.startsAt}${String(i)}`,
        a: `${formatTime(x.startsAt)} · ${name}`,
        b: `${x.area} · ${when} · in ____ out ____`,
        c: '____',
        box: true,
      })),
      {
        key: `${x.area}${x.startsAt}walk-in`,
        a: 'Walk-in',
        b: `${x.area} · ${when} · print name · phone · why here (P · C · SF · SH)`,
        c: '____________',
        box: true,
      },
    ];
  });
  return (
    <PrintRoot
      kind="letter"
      title={`The booth sheet — ${s.label}`}
      pages={[
        <Paper
          key="1"
          masthead={`${s.label} · ${formatCountDay(s.day)}`}
          submast="Sign in and out by hand when the station cannot."
          stamp={`PRINTED ${formatTime(new Date())}`}
          rows={rows.length ? rows : [{ key: 'none', a: 'No shifts on this day.' }]}
          foot="Entered at the admin station → Enter the paper. Each line becomes “from paper · entered by · date”. Entered from paper by: ______________________"
        />,
      ]}
    />
  );
}

export function WaiverPad({ copies }: { copies: number }): ReactNode {
  const d = useLoad<{ year: number; name: string | null; text: string | null }>(
    '/volunteers/booth/waiver-pad',
  );
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading the waiver…</p>;
  const w = d.data;
  const page = (n: number): ReactNode => (
    <Paper
      key={String(n)}
      masthead={`Volunteer waiver · ${String(w.year)}`}
      submast={w.name ?? 'This year’s waiver'}
      stamp="PAPER"
      rows={[
        { key: 'name', a: 'Name', c: '______________________________' },
        { key: 'sign', a: 'Signature · date · station', c: '____________  ____  ____', box: true },
        {
          key: 'parent',
          a: 'Under 18 — a parent or guardian signs here (DRAFT)',
          c: '______________________________',
          box: true,
        },
      ]}
      foot="Entered by ____________________ on ____________ · scanned onto the person as this year’s waiver."
    >
      <p className="sheet__waiver">{w.text ?? 'This year has no waiver posted.'}</p>
    </Paper>
  );
  return (
    <PrintRoot
      kind="letter"
      title={`The paper waiver pad — ${String(w.year)}`}
      pages={Array.from({ length: copies }, (_, i) => page(i))}
    />
  );
}
